// Backend #617: one row of the resolution vocabulary — the house-wide input-term table the
// automatic category/VAT resolution matches shop text against. Row identity is
// (subject, term, language): the cross-language homograph („Tee" = de tea 7 % / en T-shirt
// 19 %) exists as two rows. Targets are logical keys — a ProductCategory shortName, or the
// VAT percentage as an invariant string ("7"/"19").
export interface ResolutionVocabularyEntry {
  id: string;
  // 'ProductCategory' | 'Vat'
  subject: string;
  term: string;
  // 'de' | 'en' — or '*' for the neutral group (in scope under every content language)
  language: string;
  target: string;
  // 'Seeded' (from the seeder) | 'Curated' (born or last edited through this surface).
  // Server-owned: any edit flips a row to Curated.
  origin: string;
  // Backend #629 follow-up: birth provenance — where the term was first observed, as a
  // snapshot ("<binding rootUrl> (<customer name>)") stamped when the entry was created from
  // an observation chip. Null/undefined for shipped-dictionary and freehand entries. A
  // different dimension than origin: edits never touch it, and unlike origin it travels in
  // the JSON export (a future prod vocabulary keeps observation-backed entries, wipes the
  // starter dictionary).
  source?: string;
  // Backend #623: the sign-off stamp — "reviewed and approved by X", a different statement
  // than the revision log's "last edited by X". Set by the explicit verify action (VAT rows
  // only; the server 400s a verify on category rows) and CLEARED by every edit: an edited
  // row is an unreviewed row. Undefined/null = unverified — and an unverified VAT row blocks
  // Live VAT resolution server-side (#579 decision 4).
  // The acting subject is a raw `sub` — identity for the audit trail, not a display name.
  verifiedBy?: string;
  // ISO-8601 UTC; set and cleared together with verifiedBy.
  verifiedAt?: string;
}

export interface ResolutionVocabularyEntryPost {
  subject: string;
  term: string;
  language: string;
  target: string;
  // Backend #629 follow-up: birth provenance, sent only when the entry was taken over from
  // an observation chip and the term is still the observed one.
  source?: string;
}

// Subject is immutable server-side (it decides how target is interpreted — moving a row
// between subjects is delete + recreate). Source is immutable too: birth provenance is
// stamped at creation and no edit may rewrite where a term came from.
export type ResolutionVocabularyEntryPut = Omit<ResolutionVocabularyEntryPost, 'subject' | 'source'>;

export const ResolutionSubjectConstants = {
  ProductCategory: 'ProductCategory',
  Vat: 'Vat',
} as const;

export const NeutralLanguage = '*';

// Backend #629: the name the canonical export is saved under. It matches the backend's
// ResolutionVocabularyExport.FileName and, more importantly, the repo file the download
// replaces (Catalog.TestData/resolution-vocabulary.json) — the whole point of the round-trip
// is that the downloaded bytes are committed verbatim, so a curator saving under some other
// name has to rename it before the PR.
export const ResolutionVocabularyExportFileName = 'resolution-vocabulary.json';
